// Route-level code splitting: the Suspense fallback and the failed-chunk path
// for every lazily loaded route and workspace section.
import * as React from 'react';
import { Button } from '@/design-system/primitives/Button';

/**
 * `page` owns the viewport — a top-level route, where nothing else is painted.
 * `region` swaps inside an already-painted shell, so it holds height and stays
 * silent rather than announcing a load the surrounding chrome contradicts.
 * `overlay` is a dialog summoned over the shell: it occupies no layout while
 * loading, and reports a failure over the page rather than inside it.
 */
export type LazyVariant = 'page' | 'region' | 'overlay';

/**
 * A chunk that resolves from cache does so in a few milliseconds. Showing the
 * fallback immediately would flash an empty region on every navigation, so it
 * fades in only once the fetch is slow enough to be worth acknowledging.
 */
const FALLBACK_DELAY_MS = 160;

const fallbackKeyframes = `
@keyframes ds-lazy-fade-in { from { opacity: 0; } to { opacity: 1; } }
@media (prefers-reduced-motion: reduce) {
  .ds-lazy-fallback { animation-duration: 0s; }
}
`;

function LoadingFallback({ variant }: { variant: LazyVariant }) {
  if (variant === 'overlay') return null;
  return (
    <div
      className="ds-lazy-fallback font-body"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: variant === 'page' ? '100vh' : 240,
        opacity: 0,
        animation: `ds-lazy-fade-in var(--ds-dur-pop) var(--ds-ease-soft) ${FALLBACK_DELAY_MS}ms forwards`,
      }}
    >
      <style>{fallbackKeyframes}</style>
      <p
        role="status"
        style={{
          fontSize: 'var(--ds-text-body-sm)',
          color: 'hsl(var(--ds-text-meta))',
          // The shell already says where you are; a second label would only
          // repeat it. The text stays for assistive technology.
          ...(variant === 'region' ? { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' } : null),
        }}
      >
        Loading…
      </p>
    </div>
  );
}

function ChunkFailed({ variant, onRetry }: { variant: LazyVariant; onRetry: () => void }) {
  return (
    <div
      role="alert"
      className="font-body"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        textAlign: 'center',
        minHeight: variant === 'region' ? 240 : '100vh',
        padding: 24,
        ...(variant === 'overlay'
          ? { position: 'fixed', inset: 0, zIndex: 60, background: 'var(--ds-scrim)', backdropFilter: 'var(--ds-scrim-blur)' }
          : null),
      }}
    >
      <p style={{ fontSize: 'var(--ds-text-body-sm)', color: 'hsl(var(--ds-text-meta))', maxWidth: 340 }}>
        This didn't load. Check your connection, or reload the page if the app was updated while you were here.
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="secondary" size="sm" onClick={onRetry}>Try again</Button>
        <Button variant="ghost" size="sm" onClick={() => window.location.reload()}>Reload</Button>
      </div>
    </div>
  );
}

/**
 * A chunk fetch that failed, as opposed to a component that threw once loaded.
 * Every engine words it differently and none of them use an error subclass.
 *
 * "Failed to load module script" is the MIME rejection — a chunk that arrived
 * as `text/html` rather than JavaScript. Chrome logs that one and rejects the
 * import with the "dynamically imported module" wording, but an engine that
 * rejects with the MIME text would otherwise be treated as an application
 * crash and rethrown, losing a recoverable case.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /dynamically imported module|Importing a module script failed|Failed to load module script|error loading dynamically|ChunkLoadError/i.test(message);
}

/**
 * The chunk URL out of the error, which every engine includes in the message
 * and none exposes as a field.
 */
export function chunkUrlFrom(error: unknown): string | null {
  const message = error instanceof Error ? error.message : String(error);
  return message.match(/https?:\/\/[^\s"')]+\.js/)?.[0] ?? null;
}

/** Chunks already re-fetched this session, so a bad one cannot loop. */
const HEALED_KEY = 'ds-lazy-healed-chunks';

function healedThisSession(): string[] {
  try {
    const raw = sessionStorage.getItem(HEALED_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

/**
 * Repair a chunk whose cached response is not the chunk.
 *
 * A deploy can leave a client holding `200 text/html` under a `.js` URL — the
 * SPA fallback, cached as a success — and the module loader then refuses to
 * execute it. Reloading the page does NOT clear that: the URL is unchanged, so
 * the same poisoned entry is served again, which is why the obvious remedy is
 * the one that does not work.
 *
 * `cache: 'reload'` is the part that matters. It bypasses the HTTP cache for
 * this request AND replaces the stored entry, so the retry that follows imports
 * the real module. Once per URL per session: a chunk that is genuinely gone
 * must fail rather than refetch forever.
 */
async function healChunk(error: unknown): Promise<boolean> {
  const url = chunkUrlFrom(error);
  if (!url || healedThisSession().includes(url)) return false;
  try {
    sessionStorage.setItem(HEALED_KEY, JSON.stringify([...healedThisSession(), url]));
  } catch {
    // Private mode and the like. Proceed — the in-memory guard still holds for
    // this mount, and one extra refetch is cheaper than never recovering.
  }
  try {
    const response = await fetch(url, { cache: 'reload' });
    return response.ok && /javascript|ecmascript/i.test(response.headers.get('content-type') ?? '');
  } catch {
    return false;
  }
}

interface BoundaryProps {
  children: React.ReactNode;
  fallback: React.ReactNode;
  onChunkError: (error: unknown) => void;
}

class ChunkErrorBoundary extends React.Component<BoundaryProps, { error: Error | null }> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error) {
    if (isChunkLoadError(error)) this.props.onChunkError(error);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    // Only transport failures are ours. A render error from inside the loaded
    // route is rethrown so Sentry's boundary still reports it.
    if (!isChunkLoadError(error)) throw error;
    return this.props.fallback;
  }
}

export interface LazyRouteComponent<P> extends React.FC<P> {
  /** Start the chunk fetch before render — used for prefetch on intent. */
  preload: () => void;
}

/**
 * Wraps a dynamic import as a route component with its own loading and
 * failed-load treatment.
 */
// `React.lazy` describes its result in terms of the ref-augmented props of a
// concrete component. With props still generic, those are not provably `P`,
// though `load` resolves a `ComponentType<P>` and the element really does take
// `P`. The assertion is confined here.
function lazyComponent<P extends object>(
  load: () => Promise<{ default: React.ComponentType<P> }>,
): React.ComponentType<P> {
  return React.lazy(load) as unknown as React.ComponentType<P>;
}

export function createLazyRoute<P extends object>(
  load: () => Promise<{ default: React.ComponentType<P> }>,
  variant: LazyVariant = 'page',
): LazyRouteComponent<P> {
  const LazyRoute = (props: P) => {
    const [attempt, setAttempt] = React.useState(0);
    const [Component, setComponent] = React.useState<React.ComponentType<P>>(() => lazyComponent(load));

    // A fresh React.lazy per attempt: lazy caches its rejected promise, so
    // reusing the component would rethrow the old error without refetching.
    // The boundary is remounted by `attempt` so it drops that error too.
    const retry = React.useCallback(() => {
      setComponent(() => lazyComponent(load));
      setAttempt((a) => a + 1);
    }, []);

    // One automatic repair per mount, before the reader is shown a failure they
    // cannot act on — "reload the page" is the wrong instruction for a poisoned
    // cache entry, and the fallback stays for the cases this cannot fix.
    const healing = React.useRef(false);
    const onChunkError = React.useCallback((error: unknown) => {
      if (healing.current) return;
      healing.current = true;
      void healChunk(error).then((healed) => { if (healed) retry(); });
    }, [retry]);

    return (
      <ChunkErrorBoundary
        key={attempt}
        onChunkError={onChunkError}
        fallback={<ChunkFailed variant={variant} onRetry={retry} />}
      >
        <React.Suspense fallback={<LoadingFallback variant={variant} />}>
          <Component {...props} />
        </React.Suspense>
      </ChunkErrorBoundary>
    );
  };

  // A prefetch that fails is not the user's problem — the click that follows
  // retries it through the boundary above.
  LazyRoute.preload = () => { void load().catch(() => {}); };

  return LazyRoute;
}
